import {useTranslation} from "react-i18next";

import * as Yup from "yup";
import * as Const from "./ToolsConstants";

import {addFilter, getFilterValue, renderMultiSelect} from "../../../../../utils/FilterUtils";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_ACTIVE_FILTERS} from "../../atoms/ToolsAtoms";

import AdvancedFilter from "../../../../../component/AdvancedFilter";
import {asEndOfDay, asStartOfDay,} from "../../../../../utils/FinanceDateUtils";

import * as Renders from "../../../../../component/AdvancedFiltersRenders";
import {useLocation, useNavigate} from "react-router-dom";
import {categories, customDefaultValues, ID_FILTER_DEALERS, ID_FILTER_LEGAL_ENTITIES} from "./ToolsConstants";
import {useContext, useEffect, useState} from "react";
import {usePaymentToolsRepository} from "../../../../../api/repositories/PaymentToolsRepository";
import {GlobalContext} from "../../../../../../../util/state/GlobalContextProvider";
function ToolsFilters() {
    const {t} = useTranslation("finance", {keyPrefix: "payments"});
    const {t: tg} = useTranslation("translation");

    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get('customerId');
    const {getCountries} = usePaymentToolsRepository();
    const {globalData: { user }} = useContext(GlobalContext);

    const [countries, setCountries] = useState<any[]>([]);
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [legalEntities, setLegalEntities] = useState<string[]>(legalEntityOptions.map((x: any) => x.value));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);


    useEffect(() => {
        getCountries().then((res: any) => {
            setCountries(res);
        });
    }, []);

    const schema = Yup.object()
        .shape({
            [Const.ID_FILTER_OWNER]: Yup.string().max(
                128,
                t("filters.errors.invalid-length")
            ),
            [Const.ID_FILTER_CUSTOMER_ID]: Yup.string().max(
                128,
                t("filters.errors.invalid-length")
            ),
            [Const.ID_FILTER_CARD_TYPE]: Yup.string(),
            [Const.ID_FILTER_STATUS]: Yup.array(),
            [Const.ID_FILTER_CREATION_DATE_FROM]: Yup.date()
                .nullable()
                .test(
                    "testCreationFrom",
                    t("filters.errors.invalid-date"),
                    (value, context) => {
                        return (
                            !value ||
                            !context.parent.creationDateTo ||
                            value <= context.parent.creationDateTo
                        );
                    }
                )
                .typeError(t("filters.errors.invalid-date")),
            [Const.ID_FILTER_CREATION_DATE_TO]: Yup.date()
                .nullable()
                .test(
                    "testCreationTo",
                    t("filters.errors.invalid-date"),
                    (value, context) => {
                        return (
                            !value ||
                            !context.parent.creationDateFrom ||
                            value >= context.parent.creationDateFrom
                        );
                    }
                )
                .typeError(t("filters.errors.invalid-date")),
            [Const.ID_FILTER_DUE_DATE_FROM]: Yup.date()
                .nullable()
                .test(
                    "testDueFrom",
                    t("filters.errors.invalid-date"),
                    (value, context) => {
                        return (
                            !value ||
                            !context.parent.dueDateTo ||
                            value <= context.parent.dueDateTo
                        );
                    }
                )
                .typeError(t("filters.errors.invalid-date")),
            [Const.ID_FILTER_DUE_DATE_TO]: Yup.date()
                .nullable()
                .test(
                    "testDueTo",
                    t("filters.errors.invalid-date"),
                    (value, context) => {
                        return (
                            !value ||
                            !context.parent.dueDateFrom ||
                            value >= context.parent.dueDateFrom
                        );
                    }
                )
                .typeError(t("filters.errors.invalid-date")),
            [Const.ID_FILTER_CARD_NETWORK]: Yup.string().max(
                128,
                t("filters.errors.invalid-length")
            ),
            [Const.ID_FILTER_ISSUER]: Yup.string().max(
                128,
                t("filters.errors.invalid-length")
            ),
            [Const.ID_FILTER_CATEGORY]: Yup.string(),
            dealers: Yup.array(),
            legalEntities: Yup.array().min(1, tg("validation.selectAtLeastOne")),
        })
        .camelCase();

    const defaultValues = {
        owner: getFilterValue(Const.ID_FILTER_OWNER, filterDefs) || "",
        customerId: getFilterValue(Const.ID_FILTER_CUSTOMER_ID, filterDefs) || customerId || "",
        cardType: getFilterValue(Const.ID_FILTER_CARD_TYPE, filterDefs) || "",
        creationDateFrom: getFilterValue(Const.ID_FILTER_CREATION_DATE_FROM, filterDefs) || null,
        creationDateTo: getFilterValue(Const.ID_FILTER_CREATION_DATE_TO, filterDefs) || null,
        dueDateFrom: getFilterValue(Const.ID_FILTER_DUE_DATE_FROM, filterDefs) || null,
        dueDateTo: getFilterValue(Const.ID_FILTER_DUE_DATE_TO, filterDefs) || null,
        cardNetwork: getFilterValue(Const.ID_FILTER_CARD_NETWORK, filterDefs) || "",
        country: getFilterValue(Const.ID_FILTER_COUNTRY, filterDefs) || null,
        issuer: getFilterValue(Const.ID_FILTER_ISSUER, filterDefs) || "",
        category: getFilterValue(Const.ID_FILTER_CATEGORY, filterDefs) || "",
        correlationName: getFilterValue(Const.ID_FILTER_CORRELATION_NAME, filterDefs) || "",
        correlationId: getFilterValue(Const.ID_FILTER_CORRELATION_ID, filterDefs) || "",
        pan: getFilterValue(Const.ID_FILTER_PAN, filterDefs) || "",
        legalEntities:
            getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };

    const fields = {
        main: {
            owner: Renders.renderTextField(
                "owner",
                t("tools.filters." + Const.ID_FILTER_OWNER)
            ),
            customerId: Renders.renderTextField(
                "customerId",
                t("tools.filters." + Const.ID_FILTER_CUSTOMER_ID)
            ),
            cardType: Renders.renderSelect(
                "cardType",
                t("tools.filters." + Const.ID_FILTER_CARD_TYPE),
                Const.cardTypes.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
        },
        adv: {
            category: Renders.renderSelect(
                "category",
                t("tools.filters." + Const.ID_FILTER_CATEGORY),
                Const.categories.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            cardNetwork: Renders.renderSelect(
                "cardNetwork",
                t("tools.filters." + Const.ID_FILTER_CARD_NETWORK),
                Const.brands.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            country: Renders.renderMultiSelectCountries(
                Const.ID_FILTER_COUNTRY,
                t("tools.filters." + Const.ID_FILTER_COUNTRY),
                countries
            ),
            issuer: Renders.renderTextField(
                "issuer",
                t("tools.filters." + Const.ID_FILTER_ISSUER)
            ),
            creationDateFrom: Renders.renderDatePickerFrom(
                "creationDate",
                t("tools.filters." + Const.ID_FILTER_CREATION_DATE_FROM)
            ),
            creationDateTo: Renders.rendereDatePickerTo(
                "creationDate",
                t("tools.filters." + Const.ID_FILTER_CREATION_DATE_TO)
            ),
            dueDateFrom: Renders.renderDatePickerFrom(
                "dueDate",
                t("tools.filters." + Const.ID_FILTER_DUE_DATE_FROM)
            ),
            dueDateTo: Renders.rendereDatePickerTo(
                "dueDate",
                t("tools.filters." + Const.ID_FILTER_DUE_DATE_TO)
            ),
            correlationName: Renders.renderSelect(
                Const.ID_FILTER_CORRELATION_NAME,
                t("tools.filters." + Const.ID_FILTER_CORRELATION_NAME),
                Const.correlationName.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            correlationId: Renders.renderTextField(
                "correlationId",
                t("tools.filters." + Const.ID_FILTER_CORRELATION_ID),
                "text"
            ),
            pan: Renders.renderTextField(
                "pan",
                t("tools.filters." + Const.ID_FILTER_PAN),
                "text"
            ),
            legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
        },
    };

    const submit = (data: any) => {
        addFilter(setFilters, Const.ID_FILTER_OWNER, data.owner);
        addFilter(setFilters, Const.ID_FILTER_CUSTOMER_ID, data.customerId);
        addFilter(
            setFilters,
            Const.ID_FILTER_CREATION_DATE_FROM,
            asStartOfDay(data.creationDateFrom)
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_CREATION_DATE_TO,
            asEndOfDay(data.creationDateTo)
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_DUE_DATE_FROM,
            asStartOfDay(data.dueDateFrom)
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_DUE_DATE_TO,
            asEndOfDay(data.dueDateTo)
        );
        addFilter(setFilters, Const.ID_FILTER_CARD_NETWORK, data.cardNetwork);
        addFilter(setFilters, Const.ID_FILTER_CARD_TYPE, data.cardType);
        addFilter(setFilters, Const.ID_FILTER_COUNTRY, data.country);
        addFilter(setFilters, Const.ID_FILTER_ISSUER, data.issuer);
        addFilter(setFilters, Const.ID_FILTER_CATEGORY, data.category);
        addFilter(setFilters, Const.ID_FILTER_CORRELATION_NAME, data.correlationName);
        addFilter(setFilters, Const.ID_FILTER_CORRELATION_ID, data.correlationId);
        addFilter(setFilters, Const.ID_FILTER_PAN, data.pan);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);
        if (customerId) {
            queryParams.delete('customerId');
            const newUrl = `${location.pathname}`;
            navigate(newUrl);
        }
    };

    return (
        <AdvancedFilter
            schema={schema}
            mainFields={fields.main}
            fields={fields.adv}
            defaultValues={defaultValues}
            onSubmit={submit}
            onReset={reset}
            customDefaultValues={customDefaultValues}
        />
    );
}

export default ToolsFilters;
